import { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import Modal from "../../../../components/Modal";
import ConfirmModal from "../../../../components/ConfirmModal";
import Text from "../../../../components/Text";

import { CrossIcon, DeleteV2Icon } from "../../../../assets/img/icons";
import IconButton from "../../../../components/IconButton";
import Loader from "../../../../components/Loader";
import Mindset from "../../../../components/Mindset";
import Stat from "../../../../components/Text/Stat";
import { AuthContext } from "../../../../contexts/AuthContext";
import {
    getValidatedWeekByUserIdAndWeek,
    deleteValidatedWeek,
} from "../../../../services/api/validatedWeek";
import { GetValidatedWeekResponse } from "../../../../types/api/validatedWeek";
import { Timeslots } from "../../../../types/enums/Timeslots";
import styles from "./styles.module.scss";

const TIMESLOT_ORDER = Object.keys(Timeslots) as (keyof typeof Timeslots)[];

function getTimeslotLabel(timeslot: string): string {
    return Timeslots[timeslot as keyof typeof Timeslots] ?? timeslot;
}

function sortBreakdownByTimeslotOrder(
    items: { timeslot: string; total: number }[]
): { timeslot: string; total: number }[] {
    return [...items].sort((a, b) => {
        const i = TIMESLOT_ORDER.indexOf(a.timeslot as keyof typeof Timeslots);
        const j = TIMESLOT_ORDER.indexOf(b.timeslot as keyof typeof Timeslots);
        if (i === -1 && j === -1) return a.timeslot.localeCompare(b.timeslot);
        if (i === -1) return 1;
        if (j === -1) return -1;
        return i - j;
    });
}

export type RecapBreakdownItem = { timeslot: string; total: number };

export type RecapModalProps = {
    open: boolean;
    onClose?: () => void;
    onDeleted?: () => void;
    weekNumber?: number;
    weekResult?: string;
    year?: number;
    /** Détail du résultat par créneau (ex. 8h-10h, 15h-17h) */
    breakdownByTimeslot?: RecapBreakdownItem[];
};

function RecapModal({
    open,
    onClose,
    onDeleted,
    weekNumber,
    weekResult,
    year,
    breakdownByTimeslot = [],
}: RecapModalProps) {
    const { getUserIdFromQuery, hasPermission } = useContext(AuthContext)!;
    const [validateWeek, setValidateWeek] =
        useState<GetValidatedWeekResponse>();
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const userId = getUserIdFromQuery();
    const targetYear = year ?? new Date().getFullYear();
    const canDelete = hasPermission("admin-validated-weeks", "delete");

    useEffect(() => {
        if (open && weekNumber) {
            getValidatedWeekByUserIdAndWeek(
                getUserIdFromQuery()!,
                weekNumber,
                targetYear
            ).then((data) => {
                setValidateWeek(data);
            });
        }
    }, [open, weekNumber, year, getUserIdFromQuery, targetYear]);

    const handleConfirmDelete = async () => {
        if (!userId || weekNumber == null) return;
        setDeleteLoading(true);
        try {
            await deleteValidatedWeek(targetYear, weekNumber, userId);
            toast.success("Validation de semaine supprimée");
            setConfirmDeleteOpen(false);
            onDeleted?.();
            onClose?.();
        } catch (err: any) {
            const status = err?.response?.status;
            if (status === 404) {
                toast.error("Cette validation n'existe pas ou a déjà été supprimée.");
            } else if (status === 403) {
                toast.error("Vous n'avez pas le droit de supprimer cette validation.");
            } else {
                toast.error("Une erreur s'est produite lors de la suppression.");
            }
        } finally {
            setDeleteLoading(false);
        }
    };

    return (
        <>
            <ConfirmModal
                open={confirmDeleteOpen}
                handleClose={() => setConfirmDeleteOpen(false)}
                handleConfirm={handleConfirmDelete}
                isLoading={deleteLoading}
            />
            <Modal open={open} onClose={() => onClose?.()} size="medium" canClose>
            {weekNumber ? (
                <div className={styles.content}>
                    <div className={styles.header}>
                        <Text variant="title">{`Récapitulatif de la semaine`}</Text>
                            <div className={styles.headerActions}>
                                {canDelete && (
                                    <button
                                        type="button"
                                        className={styles.deleteButton}
                                        onClick={() => setConfirmDeleteOpen(true)}
                                        title="Supprimer la validation"
                                    >
                                        <DeleteV2Icon
                                            className={styles.deleteIcon}
                                            width={18}
                                            height={18}
                                        />
                                    </button>
                                )}
                                {onClose && (
                                    <IconButton
                                        icon={CrossIcon}
                                        color={"secondary"}
                                        onClick={onClose}
                                    />
                                )}
                            </div>
                        </div>
                    <div className={styles.summaryBox}>
                        <div className={styles.summaryItem}>
                            <span className={styles.summaryLabel}>État d'esprit</span>
                            <div className={styles.summaryValue}>
                                {validateWeek?.mindset && (
                                    <Mindset mindset={validateWeek?.mindset} />
                                )}
                            </div>
                        </div>
                        <div className={styles.summaryItem}>
                            <span className={styles.summaryLabel}>Résultat de la semaine</span>
                            <div className={styles.summaryValue}>
                                <Stat variant="subtitle" type="currency">
                                    {weekResult}
                                </Stat>
                            </div>
                            {breakdownByTimeslot.length > 0 && (
                                <div className={styles.timeslotBreakdown}>
                                    <span className={styles.timeslotBreakdownLabel}>
                                        Détail par créneau
                                    </span>
                                    <ul className={styles.timeslotBreakdownList}>
                                        {sortBreakdownByTimeslotOrder(breakdownByTimeslot).map(({ timeslot, total }) => (
                                            <li key={timeslot} className={styles.timeslotBreakdownRow}>
                                                <span className={styles.timeslotName}>{getTimeslotLabel(timeslot)}</span>
                                                <Stat variant="body" type="currency">
                                                    {total}
                                                </Stat>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                        <div className={styles.summaryItem}>
                            <span className={styles.summaryLabel}>Commentaire</span>
                            <p className={styles.summaryText}>
                                {validateWeek?.comment?.trim()
                                    ? validateWeek.comment
                                    : "Aucun commentaire"}
                            </p>
                        </div>
                    </div>
                    {validateWeek?.fileUrl ? (
                        <div className={styles.imageSection}>
                            <span className={styles.summaryLabel}>Historique du compte</span>
                            <img
                                src={validateWeek.fileUrl}
                                alt="Historique du compte"
                                className={styles.imagePreview}
                            />
                        </div>
                    ) : (
                        <p className={styles.noFile}>Aucun fichier disponible.</p>
                    )}
                </div>
                ) : (
                    <Loader />
                )}
            </Modal>
        </>
    );
}

export default RecapModal;
